import type { CSSProperties } from 'react';
import type { TooltipPosition } from './useSmartTooltipPosition';

/**
 * Determines the flipped position of the tooltip based on the preferred position,
 * anchor rectangle, tooltip rectangle, viewport dimensions, and a threshold.
 * This function checks if the tooltip would overflow the viewport and flips the position
 * accordingly to ensure the tooltip remains visible.
 * @param preferredPosition - The initially preferred position of the tooltip.
 * @param anchorRect - The bounding rectangle of the anchor element.
 * @param tooltipRect - The bounding rectangle of the tooltip element.
 * @param viewport - The dimensions of the viewport.
 * @param threshold - The minimum distance from the viewport edges.
 * @returns - The final position of the tooltip after applying flip logic.
 */
export function getFlippedPosition(
  preferredPosition: TooltipPosition,
  anchorRect: DOMRect,
  tooltipRect: DOMRect,
  viewport: { width: number; height: number },
  threshold: number
): TooltipPosition {
  let final = preferredPosition;

  if (
    preferredPosition === 'down' &&
    anchorRect.bottom + tooltipRect.height > viewport.height - threshold
  ) {
    final = 'up';
  } else if (
    preferredPosition === 'up' &&
    anchorRect.top - tooltipRect.height < threshold
  ) {
    final = 'down';
  } else if (
    preferredPosition === 'right' &&
    anchorRect.right + tooltipRect.width > viewport.width - threshold
  ) {
    final = 'down';
  } else if (
    preferredPosition === 'left' &&
    anchorRect.left - tooltipRect.width < threshold
  ) {
    final = 'down';
  }

  return final;
}

/**
 * Calculates the base position of the tooltip based on the anchor element's
 * position and the tooltip's dimensions. It sets the top and left styles
 * to position the tooltip relative to the anchor element according to the specified position.
 * @param position - The position of the tooltip.
 * @param anchorRect - The bounding rectangle of the anchor element.
 * @param tooltipRect - The bounding rectangle of the tooltip element.
 * @param scrollX - The horizontal scroll position.
 * @returns - The CSS styles to apply to the tooltip for positioning.
 */
export function calculateBasePosition(
  position: TooltipPosition,
  anchorRect: DOMRect,
  tooltipRect: DOMRect,
  scrollX: number
): CSSProperties {
  const styles: CSSProperties = {};

  switch (position) {
    case 'down':
      styles.top = anchorRect.bottom + 10;
      styles.left =
        anchorRect.left + (anchorRect.width - tooltipRect.width) / 2 + scrollX;
      break;
    case 'up':
      styles.top = anchorRect.top - tooltipRect.height + 5;
      styles.left =
        anchorRect.left + (anchorRect.width - tooltipRect.width) / 2 + scrollX;
      break;
    case 'right':
      styles.top =
        anchorRect.top + (anchorRect.height - tooltipRect.height) / 2;
      styles.left = anchorRect.right + scrollX + 18;
      break;
    case 'left':
      styles.top =
        anchorRect.top + (anchorRect.height - tooltipRect.height) / 2;
      styles.left = anchorRect.left - tooltipRect.width - 18;
      break;
  }

  return styles;
}

/**
 * Applies nudge logic to the tooltip styles to ensure it does not overflow
 * the viewport boundaries. It adjusts the left and top styles based on a threshold
 * to keep the tooltip within the visible area of the viewport.
 * @param styles - The CSS styles to apply to the tooltip.
 * @param tooltipRect - The bounding rectangle of the tooltip element.
 * @param viewport - The dimensions of the viewport.
 * @param threshold - The minimum distance from the viewport edges.
 * @returns - The adjusted CSS styles for the tooltip.
 */
export function applyNudge(
  styles: CSSProperties,
  tooltipRect: DOMRect,
  viewport: { width: number; height: number },
  threshold: number
): CSSProperties {
  const adjusted = { ...styles };

  if (typeof adjusted.left === 'number') {
    if (adjusted.left < threshold) {
      adjusted.left = threshold;
    } else if (adjusted.left + tooltipRect.width > viewport.width - threshold) {
      adjusted.left = viewport.width - tooltipRect.width - threshold;
    }
  }

  if (typeof adjusted.top === 'number') {
    if (adjusted.top < threshold) {
      adjusted.top = threshold;
    } else if (
      adjusted.top + tooltipRect.height >
      viewport.height - threshold
    ) {
      adjusted.top = viewport.height - tooltipRect.height - threshold;
    }
  }

  return adjusted;
}

/**
 * Calculates the offset for the arrow of the tooltip based on its position
 * relative to the anchor element. It adjusts the left or top style of the arrow
 * to align it with the center of the anchor element.
 * @param position - The position of the tooltip.
 * @param anchorRect - The bounding rectangle of the anchor element.
 * @param styles - The CSS styles applied to the tooltip.
 * @returns - The calculated CSS styles for the arrow of the tooltip.
 */
export function calculateArrowOffset(
  position: TooltipPosition,
  anchorRect: DOMRect,
  styles: CSSProperties
): CSSProperties {
  const arrow: CSSProperties = {};

  if (position === 'up' || position === 'down') {
    const anchorCenterX = anchorRect.left + anchorRect.width / 2;
    arrow.left = anchorCenterX - (styles.left as number) - 18; // half arrow width
  } else if (position === 'left' || position === 'right') {
    const anchorCenterY = anchorRect.top + anchorRect.height / 2;
    arrow.top = anchorCenterY - ((styles.top as number) + 18); // half arrow height
  }

  return arrow;
}
